import React, { Fragment, useEffect, useMemo, useReducer, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom/cjs/react-router-dom.min';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Drawer, Select } from 'antd';

// Images
import H3Image from '../../../../uilib/h3Image';
import closeIcon from '@Images/icons/icon-close-blue.png';

// Components
import AccountPayableInvoiceListHeader from './AccountPayableInvoiceListHeader';
import AccountPayableInvoiceListTable from './AccountPayableInvoiceListTable';
import TenantSelector from '@Components/Common/Selector/TenantSelector';
import SelectDepartment from '@Components/Common/SelectDepartment';
import TagSelector from '@Components/Common/Selector/TagSelector';
import H3Text from '../../../../uilib/h3Text';
import MoreInfoMobileView from '../../../Common/MoreInfoMobileView';
import { ListScreenKeys } from '../../GoodsReceiving/GoodsReceivingHome/ViewSTGRN/mobileListHelper';

// Hooks
import { useApiQuery } from '../../../../apiV2/hooks/useApiQuery';

// Helpers
import Helpers from '../../../../apis/helpers';
import PRZSelect from '../../../Common/UI/PRZSelect';
import dayjs from 'dayjs';
import { dateRangePresets, dateRangePresetsValues, entityNameEnum, filterTypeEnums } from '../../../../apis/constants';

// Reducers
import { filterReducer, initialFilterState } from './reducers';

// Actions
import CFV2Actions from '@Actions/configurations/cfV2Actions';
const { Option } = Select;

const AccountPayableInvoiceList = ({
  user, priceMasking, MONEY, customFieldsProps, getDocCFV2Loading, history, getDocCFV2, getDocCFV2Success, departments,
}) => {
  const [isUserReady, setIsUserReady] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [openPopover, setOpenPopover] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterChanged, setFilterChanged] = useState(false);
  const [firstTimeFilterDisable, setFirstTimeFilterDisable] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState('');
  const [moreDetailsDrawer, setMoreDetailsDrawer] = useState(false);
  const [selectedAPInvoice, setSelectedAPInvoice] = useState(null);

  const [currentDefaultView, setCurrentDefaultView] = useState({});
  const [currentlyAppliedFilters, setCurrentlyAppliedFilters] = useState([]);
  const [isUpdateFilterState, setIsUpdateFilterState] = useState(true);
  const [savedFilterViews, setSavedFilterViews] = useState([]);
  const [selectedFilterView, setSelectedFilterView] = useState({});
  const [savedFilterViewsLoading, setSavedFilterViewsLoading] = useState(false);
  const [apInvoiceSelectedIntervalDateRangeType, setApInvoiceSelectedIntervalDateRangeType] = useState('');
  const [isUserReadyInitial, setIsUserReadyInitial] = useState(false);

  const fixedColumns = useMemo(
    () => ['AP_INVOICE_NUMBER', 'VENDOR', 'LOCATION'],
    [],
  );
  const [currentUrl, setCurrentUrl] = useState('');

  const [visibleColumns, setVisibleColumns] = useState(
    localStorage.getItem('prcz-page-columns-ap-invoice')
      ? JSON.parse(localStorage.getItem('prcz-page-columns-ap-invoice'))
      : {
        AP_INVOICE_NUMBER: {
          label: 'AP Invoice#',
          visible: true,
        },
        LOCATION: {
          label: 'Location',
          visible: true,
        },
        VENDOR: {
          label: 'Vendor',
          visible: true,
        },
        VENDOR_INVOICE: {
          label: 'Vendor Invoice',
          visible: true,
        },
        REFERENCE: {
          label: 'Reference#',
          visible: true,
        },
        AMOUNT_WITHOUT_TAX: {
          label: 'Amount (Without Tax)',
          visible: false,
        },
        TAXES: {
          label: 'Taxes',
          visible: false,
        },
        GRAND_TOTAL: {
          label: 'Total Amount',
          visible: true,
        },
        CREATED_TIME: {
          label: 'Created Date',
          visible: true,
        },
        AP_INOICE_DATE: {
          label: 'Account Payable Invoice Date',
          visible: true,
        },
        TOTAL_DUE: {
          label: 'Due Amount',
          visible: true,
        },
        EWB_NUMBER: {
          label: 'EWB Number',
          visible: true,
        },
        ATTACHMENTS: {
          label: 'Attachments',
          visible: true,
        },
        STATUS: {
          label: 'Status',
          visible: true,
        },
        PAYMENT_STATUS: {
          label: 'Payment Status',
          visible: true,
        },
        LABEL: {
          label: 'Label',
          visible: true,
        },
      },
  );
  const [filterState, dispatch] = useReducer(filterReducer, initialFilterState);

  const {
    selectedTenant,
    selectedDepartment,
    selectedStatus,
    paymentStatus,
    selectedTag,
  } = filterState;

  const { data, isLoading, refetch } = useApiQuery({
    key: ['apInvoices'],
    url: '/account-payable-invoice/list',
    params: {
      page: currentPage,
      limit: limit,
      search_keyword: searchKeyword,
      from_date: startDate,
      to_date: endDate,
      tenant_id:
        filterState?.selectedTenant?.length > 0
          ? filterState?.selectedTenant?.join(',')
          : Helpers.getTenantEntityPermission(
            user?.user_tenants,
            Helpers.permissionEntities.ACCOUNT_PAYABLE_INVOICE,
            Helpers.permissionTypes.READ,
          )?.join(','),
      department_ids: filterState?.selectedDepartment?.join(','),
      status: filterState?.selectedStatus,
      payment_status: filterState?.paymentStatus?.join(','),
      tags: filterState?.selectedTag?.join(','),
    },
    enabled: false,
  });

  const queryClient = useQueryClient();
  const cachedData = queryClient.getQueryData(['apInvoices']);

  const initialFilterValues = useMemo(
    () => [
      {
        filter_key: 'limit',
        filter_value: limit,
        filter_type: filterTypeEnums.NUMBER,
      },
      {
        filter_key: 'tenant',
        filter_value: [user?.tenant_info?.tenant_id],
        filter_type: filterTypeEnums.ARRAY,
      },
      {
        filter_key: 'status',
        filter_value: '',
        filter_type: filterTypeEnums.STRING,
      },
      {
        filter_key: 'keyword',
        filter_value: '',
        filter_type: filterTypeEnums.STRING,
      },
      {
        filter_key: 'startDate',
        filter_value: '',
        filter_type: filterTypeEnums.DATE_RANGE,
        date_range_type: '',
      },
      {
        filter_key: 'endDate',
        filter_value: '',
        filter_type: filterTypeEnums.DATE_RANGE,
        date_range_type: '',
      },
      {
        filter_key: 'payment_status',
        filter_value: [],
        filter_type: filterTypeEnums.ARRAY,
      },
      {
        filter_key: 'label',
        filter_value: [],
        filter_type: filterTypeEnums.ARRAY,
      },
      {
        filter_key: 'department_ids',
        filter_value: [],
        filter_type: filterTypeEnums.ARRAY,
      },
    ],
    [],
  );

  function getAppliedFiltersFromURL() {
    const appliedFiltersFromURL = [
      {
        filter_key: 'limit',
        filter_value: Number(Helpers.getValueFromUrl('limit') || limit),
        filter_type: filterTypeEnums.NUMBER,
      },
      {
        filter_key: 'tenant',
        filter_value: Helpers.getValueFromUrl('tenant')
          ? Helpers.getValueFromUrl('tenant')
            .split(',')
            .map((item) => Number(item))
          : [],
        filter_type: filterTypeEnums.ARRAY,
      },
      {
        filter_key: 'status',
        filter_value: Helpers.getValueFromUrl('status') || '',
        filter_type: filterTypeEnums.STRING,
      },
      {
        filter_key: 'keyword',
        filter_value: Helpers.getValueFromUrl('keyword') || '',
        filter_type: filterTypeEnums.STRING,
      },
      {
        filter_key: 'startDate',
        filter_value: Helpers.getValueFromUrl('startDate')
          ? Number(dayjs(Helpers.getValueFromUrl('startDate')).startOf('day'))
          : '',
        filter_type: filterTypeEnums.DATE_RANGE,
        date_range_type: Helpers.getValueFromUrl('ap_invoice_date_type') || '',
      },
      {
        filter_key: 'endDate',
        filter_value: Helpers.getValueFromUrl('endDate')
          ? Number(dayjs(Helpers.getValueFromUrl('endDate')).endOf('day'))
          : '',
        filter_type: filterTypeEnums.DATE_RANGE,
        date_range_type: Helpers.getValueFromUrl('ap_invoice_date_type') || '',
      },
      {
        filter_key: 'payment_status',
        filter_value: Helpers.getValueFromUrl('payment_status')
          ? Helpers.getValueFromUrl('payment_status').split(',')
          : [],
        filter_type: filterTypeEnums.ARRAY,
      },
      {
        filter_key: 'label',
        filter_value: Helpers.getValueFromUrl('label')
          ? Helpers.getValueFromUrl('label').split(',')
          : [],
        filter_type: filterTypeEnums.ARRAY,
      },
      {
        filter_key: 'department_ids',
        filter_value: Helpers.getValueFromUrl('department_ids')
          ? Helpers.getValueFromUrl('department_ids')
            .split(',')
            .map((item) => Number(item))
          : [],
        filter_type: filterTypeEnums.ARRAY,
      },
    ];
    return appliedFiltersFromURL;
  }

  function getFilterValueByKey({ filtersArray, key, valueOnly = true }) {
    const selectedFilter =
      filtersArray.find((item) => item.filter_key === key) || null;
    return valueOnly ? selectedFilter?.filter_value : selectedFilter;
  }

  const systemDefaultUserView = useMemo(
    () => ({
      filter_id: 'system_default',
      filter_name: 'System Default View',
      entity_name: entityNameEnum.ACCOUNT_PAYABLE_INVOICE,
      filters: initialFilterValues,
      tenant_id: user?.tenant_info?.tenant_id,
      org_id: user?.user_org_id,
      user_id: user?.user_id,
      is_default: false,
      is_system_default: true,
    }),
    [],
  );

  useEffect(() => {
    if (customFieldsProps?.data?.document_custom_fields?.length) {
      const customFields = customFieldsProps?.data?.document_custom_fields;
      const columns = JSON.parse(JSON.stringify(visibleColumns));
      customFields
        ?.filter((item) => item?.is_active)
        ?.map((customField) => {
          columns[customField.cf_id] = {
            label: customField?.field_name,
            visible:
              JSON.parse(
                localStorage.getItem('prcz-page-columns-ap-invoice'),
              )?.[customField.cf_id]?.visible || false,
          };
        });
      setVisibleColumns(columns);
    }
  }, [customFieldsProps]);

  useEffect(() => {
    if (user?.tenant_info?.tenant_id) {
      dispatch({
        type: 'SET_INITIAL_TENANT',
        payload: [user?.tenant_info?.tenant_id],
      });
    }
  }, [user]);

  useEffect(() => {
    if (firstTimeFilterDisable) {
      setFilterChanged(true);
    }
  }, [filterState]);

  useEffect(() => {
    if (user?.tenant_info?.org_id) {

      setSelectedFilterView(systemDefaultUserView);
    }
    const payload = {
      orgId: user?.tenant_info?.org_id,
      entityName: 'ACCOUNT_PAYABLE_INVOICE',
    };
    getDocCFV2(payload);
    return () => {
      getDocCFV2Success(null);
    };
  }, []);

  useEffect(() => {
    if (savedFilterViews && isUserReadyInitial) {
      const defaultUserView = savedFilterViews.find((item) => item.is_default);

      if (defaultUserView) {
        setCurrentDefaultView(defaultUserView);
      } else {
        setCurrentDefaultView(systemDefaultUserView);
      }

      const appliedFiltersFromURL = getAppliedFiltersFromURL();

      const selectedFilterViewFromURL = Helpers.getValueFromUrl('filter_id');

      let currentSelectedView;
      if (selectedFilterViewFromURL) {
        currentSelectedView = savedFilterViews.find(
          (item) => String(item.filter_id) === String(selectedFilterViewFromURL),
        );
        if (!currentSelectedView) {
          currentSelectedView = systemDefaultUserView;
        }
      } else {
        currentSelectedView = defaultUserView || systemDefaultUserView;
      }

      const filterValues = (isFromURL) => {
        const currentSelectedViewFilters = currentSelectedView.filters;

        const filterValue = initialFilterValues
          .map((item) => item.filter_key)
          .reduce(
            (acc, filterKey) => ({
              ...acc,
              [filterKey]: getFilterValueByKey({
                filtersArray: isFromURL
                  ? appliedFiltersFromURL
                  : currentSelectedViewFilters,
                key: filterKey,
              }),
            }),
            {},
          );

        const _orderDateRangeType = getFilterValueByKey({
          filtersArray: isFromURL
            ? appliedFiltersFromURL
            : currentSelectedViewFilters,
          key: 'startDate',
          valueOnly: false,
        })?.date_range_type;

        let _orderStartDate = '';
        let _orderEndDate = '';

        if (
          _orderDateRangeType === '' ||
          _orderDateRangeType === dateRangePresets.CUSTOM
        ) {
          // No action needed for empty or custom date range type
        } else {
          const date = dateRangePresetsValues(_orderDateRangeType);
          const [start, end] = date;

          _orderStartDate = start;
          _orderEndDate = end;
          filterValue.from_date = _orderStartDate;
          filterValue.to_date = _orderEndDate;
        }

        filterValue.orderDateRangeType = _orderDateRangeType;

        return filterValue;
      };

      const tempFilterValues = filterValues(selectedFilterViewFromURL);

      setSelectedFilterView(currentSelectedView);
      if (isUpdateFilterState) {
        setCurrentlyAppliedFilters(appliedFiltersFromURL);

        setSelectedEmails([user?.email]);

        setCurrentPage(Number(Helpers.getValueFromUrl('page') || currentPage));
        setLimit(tempFilterValues.limit || limit);
        setSearchKeyword(tempFilterValues.keyword);
        setKeyword(tempFilterValues.keyword);
        setStartDate(tempFilterValues.from_date);
        setEndDate(tempFilterValues.to_date);
        setApInvoiceSelectedIntervalDateRangeType(
          tempFilterValues.orderDateRangeType,
        );

        dispatch({
          type: 'UPDATE_URL',
          payload: {
            selectedTenant: tempFilterValues.tenant,
            selectedDepartment: tempFilterValues.department_ids,
            selectedStatus: tempFilterValues.status,
            paymentStatus: tempFilterValues.payment_status,
            selectedTag: tempFilterValues.label,
          },
        });

        pushParamsToUrl({
          currentKeyword: searchKeyword,
          page: Number(Helpers.getValueFromUrl('page') || currentPage),
          currentLimit: tempFilterValues.limit || limit,
          filterState: {
            selectedTenant: tempFilterValues.tenant,
            selectedDepartment: tempFilterValues.department_ids,
            selectedStatus: tempFilterValues.status,
            paymentStatus: tempFilterValues.payment_status,
            selectedTag: tempFilterValues.label,
          },
          startDate: tempFilterValues.from_date,
          endDate: tempFilterValues.to_date,
          apInvoiceSelectedIntervalDateRangeType:
            tempFilterValues.orderDateRangeType,
          selectedFilterView: currentSelectedView,
        });
      }

      if (!isUserReady) setIsUserReady(true);
      if (!isUpdateFilterState) setIsUpdateFilterState(true);
    }
  }, [savedFilterViews, isUserReadyInitial]);

  const handleApplyFilters = () => {
    setFirstTimeFilterDisable(true);
    setFilterChanged(false);
    setShowFilters(false);
    pushParamsToUrl({
      currentKeyword: searchKeyword,
      page: 1,
      currentLimit: limit,
      filterState,
      startDate,
      endDate,
    });
  };

  const pushParamsToUrl = ({
    currentKeyword,
    page,
    currentLimit,
    filterState,
    startDate,
    endDate,
    apInvoiceSelectedIntervalDateRangeType,
    selectedFilterView,
  }) => {
    let url = '';
    if (page) url += `?page=${page}`;
    if (currentLimit) url += `&limit=${currentLimit}`;
    if (currentKeyword) url += `&keyword=${currentKeyword}`;
    if (filterState?.selectedTenant) { url += `&tenant=${filterState.selectedTenant.join(',')}`; }
    if (filterState?.selectedDepartment) { url += `&department_ids=${filterState?.selectedDepartment?.join(',')}`; }
    if (filterState?.selectedStatus) { url += `&status=${filterState?.selectedStatus}`; }
    if (filterState?.due_date?.from) { url += `&from_date=${filterState?.due_date?.from}`; }
    if (filterState?.due_date?.to) { url += `&to_date=${filterState?.due_date?.to}`; }
    if (apInvoiceSelectedIntervalDateRangeType) { url += `&ap_invoice_date_type=${apInvoiceSelectedIntervalDateRangeType}`; }
    if (filterState?.paymentStatus) { url += `&payment_status=${filterState?.paymentStatus?.join(',')}`; }
    if (filterState?.selectedTag) { url += `&label=${filterState?.selectedTag?.join(',')}`; }
    if (startDate) url += `&startDate=${startDate}`;
    if (endDate) url += `&endDate=${endDate}`;
    if (selectedFilterView) { url += `&filter_id=${selectedFilterView?.filter_id}`; }
    setCurrentUrl(url);
    history.push(url);
  };

  useEffect(() => {
    if (isUserReady) {
      setCurrentlyAppliedFilters(getAppliedFiltersFromURL());
      refetch();
    }
  }, [currentUrl]);

  const apinvoiceOptions = [
    { key: '', value: '', label: 'All Account Payable Invoices' },
    { key: 'DRAFT', value: 'DRAFT', label: 'Draft' },
    { key: 'ISSUED', value: 'ISSUED', label: 'Issued' },
    { key: 'REJECTED', value: 'REJECTED', label: 'Rejected' },
    { key: 'VOID', value: 'VOID', label: 'Void' },
    {
      key: 'SENT_FOR_APPROVAL',
      value: 'SENT_FOR_APPROVAL',
      label: 'Sent For Approval',
    },
  ];

  return (
    <Fragment>
      <Drawer
        open={showFilters}
        onClose={() => setShowFilters(false)}
        showHeader
        width="360px"
        destroyOnClose
      >
        <div className="custom-drawer__header-wrapper">
          <div className="custom-drawer__header" style={{ width: '310px' }}>
            <H3Text text="Apply Filters" className="custom-drawer__title" />
            <H3Image
              src={closeIcon}
              className="custom-drawer__close-icon"
              onClick={() => setShowFilters(false)}
            />
          </div>
        </div>
        <div className="filter__container">
          <div className="filter__title">Business Unit</div>
          <TenantSelector
            selectedTenant={selectedTenant}
            showSearch
            onChange={(value) => {
              setFirstTimeFilterDisable(true);
              dispatch({ type: 'SET_TENANT', payload: value });
            }}
            placeholder="Select Business Unit"
            inputClassName="orgInputContainer"
            isMultiple
            hideTitle
            includedTenants={Helpers.getTenantEntityPermission(
              user?.user_tenants,
              Helpers.permissionEntities.ACCOUNT_PAYABLE_INVOICE,
              Helpers.permissionTypes.READ,
            )}
          />
        </div>
        <div className="filter__container">
          <div className="filter__title">Departments</div>
          <SelectDepartment
            title="Select Department"
            hideTitle
            selectedDepartment={selectedDepartment}
            noDropdownAlign
            onChange={(value) => {
              setFirstTimeFilterDisable(true);
              dispatch({ type: 'SET_DEPARTMENT', payload: value });
            }}
            isMultiple
          />
        </div>
        <div className="filter__container" style={{ marginBottom: '10px' }}>
          <div className="filter__title">Status</div>
          <PRZSelect
            value={selectedStatus}
            onChange={(value) => {
              setFirstTimeFilterDisable(true);
              dispatch({ type: 'SET_STATUS', payload: value });
              setCurrentPage(1);
            }}
            placeholder="Filter by status.."
            options={apinvoiceOptions}
          />
        </div>
        <div className="filter__container" style={{ marginBottom: '10px' }}>
          <div className="filter__title">Payment Status</div>
          <PRZSelect
            dropdownAlign={{ offset: [3, 4] }}
            value={paymentStatus}
            onChange={(value) => {
              setFirstTimeFilterDisable(true);
              dispatch({ type: 'SET_PAYMENT_STATUS', payload: value });
              setCurrentPage(1);
            }}
            placeholder="Search by Payment Status.."
            className="purchase-orders__table-header__filter"
            mode="multiple"
          >
            <Option key="PAID_MORE" value="PAID_MORE">
              Paid More
            </Option>
            <Option key="PARTIALLY_PAID" value="PARTIALLY_PAID">
              Partially Paid
            </Option>
            <Option key="PAID" value="PAID">
              Paid
            </Option>
            <Option key="NOT_PAID" value="NOT_PAID">
              Not Paid
            </Option>
          </PRZSelect>
        </div>
        <div className="filter__container" style={{ marginBottom: '10px' }}>
          <div className="filter__title"> Select Labels </div>
          <div>
            <TagSelector
              filter
              entityType="GRN"
              selectedTags={selectedTag}
              showSearch
              showAll={false}
              noDropdownAlign
              isMultiple
              onChange={(value) => {
                setFirstTimeFilterDisable(true);
                setCurrentPage(1);
                dispatch({ type: 'SET_TAG', payload: value });
              }}
              placeholder="Select Label"
              inputClassName="orgInputContainer"
              hideTitle
              hideCSS
            />
          </div>
        </div>
        <Button
          type="primary"
          onClick={() => {
            setShowFilters(false);
            handleApplyFilters();
          }}
          disabled={!filterChanged}
          block
        >
          Apply Filters
        </Button>
      </Drawer>
      <Drawer
        open={moreDetailsDrawer}
        mask
        onClose={() => setMoreDetailsDrawer(false)}
        destroyOnClose
        rootClassName="mobile__more-details_wrapper"
      >
        <div className="custom-drawer__header-wrapper">
          <div
            className="custom-drawer__header"
          >
            <H3Text
              text={`${selectedAPInvoice?.grn_number}`}
              className="custom-drawer__title"
            />
            <H3Image
              src={closeIcon}
              className="custom-drawer__close-icon"
              onClick={() => setMoreDetailsDrawer(false)}
            />
          </div>
        </div>
        <MoreInfoMobileView
          customFields={selectedAPInvoice?.custom_fields}
          fields={ListScreenKeys({ documentData: selectedAPInvoice, MONEY, priceMasking })}
        />
      </Drawer>
      <div className="doc-list__wrapper">
        <div className="ant-row">
          <AccountPayableInvoiceListHeader
            user={user}
            keyword={keyword}
            setKeyword={setKeyword}
            searchKeyword={searchKeyword}
            setSearchKeyword={setSearchKeyword}
            history={history}
            filterState={filterState}
            dispatch={dispatch}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            departments={departments}
            setCurrentPage={setCurrentPage}
            setLimit={setLimit}
            setFirstTimeFilterDisable={setFirstTimeFilterDisable}
            pushParamsToUrl={({
              currentKeyword,
              page,
              currentLimit,
              filterState,
              startDate,
              endDate,
            }) =>
              pushParamsToUrl({
                currentKeyword,
                page,
                currentLimit,
                filterState,
                startDate,
                endDate,
              })
            }
            refetch={refetch}
            handleApplyFilters={handleApplyFilters}
            selectedFilterView={selectedFilterView}
            setSelectedFilterView={setSelectedFilterView}
            getFilterValueByKey={getFilterValueByKey}
            initialFilterValues={initialFilterValues}
            currentlyAppliedFilters={currentlyAppliedFilters}
            isUserReadyInitial={isUserReadyInitial}
            setIsUserReadyInitial={setIsUserReadyInitial}
            setSavedFilterViews={setSavedFilterViews}
            setSavedFilterViewsLoading={setSavedFilterViewsLoading}
            setIsUpdateFilterState={setIsUpdateFilterState}
            systemDefaultUserView={systemDefaultUserView}
            apInvoiceSelectedIntervalDateRangeType={
              apInvoiceSelectedIntervalDateRangeType
            }
            setApInvoiceSelectedIntervalDateRangeType={
              setApInvoiceSelectedIntervalDateRangeType
            }
          />
          <AccountPayableInvoiceListTable
            data={data}
            setCurrentPage={setCurrentPage}
            setLimit={setLimit}
            visibleColumns={visibleColumns}
            currentPage={currentPage}
            limit={limit}
            isLoading={
              isLoading || getDocCFV2Loading || savedFilterViewsLoading
            }
            user={user}
            priceMasking={priceMasking}
            MONEY={MONEY}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            openPopover={openPopover}
            setOpenPopover={setOpenPopover}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            fixedColumns={fixedColumns}
            customFieldsProps={customFieldsProps}
            setVisibleColumns={setVisibleColumns}
            pushParamsToUrl={pushParamsToUrl}
            searchKeyword={searchKeyword}
            filterState={filterState}
            apInvoiceSelectedIntervalDateRangeType={
              apInvoiceSelectedIntervalDateRangeType
            }
            setApInvoiceSelectedIntervalDateRangeType={
              setApInvoiceSelectedIntervalDateRangeType
            }
            setMoreDetailsDrawer={setMoreDetailsDrawer}
            setSelectedAPInvoice={setSelectedAPInvoice}
          />
        </div>
      </div>
    </Fragment>
  );
};

const mapDispatchToProps = (dispatch) => ({
  getDocCFV2: (payload, callback) =>
    dispatch(CFV2Actions.getDocCFV2(payload, callback)),
  getDocCFV2Success: (cf) => dispatch(CFV2Actions.getDocCFV2Success(cf)),
});

const mapStateToProps = ({
  UserReducers,
  DepartmentReducers,
  CFV2Reducers,
}) => ({
  user: UserReducers.user,
  MONEY: UserReducers.MONEY,
  departments: DepartmentReducers.departments,
  priceMasking: UserReducers.priceMasking,
  customFieldsProps: CFV2Reducers.cfV2DocAPInvoice,
  getDocCFV2Loading: CFV2Reducers.getDocCFV2Loading,
});

// export default AccountPayableInvoiceList
export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(AccountPayableInvoiceList));
